import { useState, useRef, useEffect, useCallback, type FormEvent } from 'react';
import type { FitPreference, BudgetTier, ConditionOverrides } from '@nfa/types';
import { useSearchHistory, type SearchHistoryItem } from '../hooks/useSearchHistory';

export interface QuerySubmitOptions {
  query: string;
  fit: FitPreference;
  budget_tier?: BudgetTier;
  conditions?: ConditionOverrides;
}

interface QueryInputProps {
  onSubmit: (options: QuerySubmitOptions) => void;
  isLoading: boolean;
  initialQuery?: string;
}

/**
 * Example query data with route images
 * Images are from Unsplash (royalty-free)
 */
interface ExampleQueryData {
  id: string;
  name: string;
  query: string;
  imageUrl: string;
}

/**
 * Example queries with VERIFIED location-accurate Unsplash images
 * Each image has been manually verified to show the actual trail/location
 *
 * Image sources (all free Unsplash License):
 * - West Highland Way: Buachaille Etive Beag, Glencoe by Cat Bassano
 * - Cape Wrath Trail: Ben Wyvis, Scottish Highlands by Ross Sneddon
 * - Lake District: Haystacks, Buttermere by Josh Kirk
 * - Coast to Coast: Yorkshire Dales sheep & dry stone walls by Donna Elliot
 */
const EXAMPLE_QUERIES: ExampleQueryData[] = [
  {
    id: 'west-highland-way',
    name: 'West Highland Way',
    query: '5 days on the West Highland Way in June',
    // VERIFIED: Hikers on Buachaille Etive Beag, Glencoe, Scotland
    imageUrl: 'https://images.unsplash.com/photo-1556008132-75d56bdf1f39?w=400&h=300&fit=crop',
  },
  {
    id: 'cape-wrath-trail',
    name: 'Cape Wrath Trail',
    query: 'Cape Wrath Trail in September, ultralight',
    // VERIFIED: Ben Wyvis, remote Scottish Highlands wilderness
    imageUrl: 'https://images.unsplash.com/photo-1631531756906-f256d7c94447?w=400&h=300&fit=crop',
  },
  {
    id: 'lake-district',
    name: 'Lake District',
    query: 'Weekend in the Lake District, budget kit',
    // VERIFIED: Haystacks, Buttermere, Lake District
    imageUrl: 'https://images.unsplash.com/photo-1628616043557-78460cc89ab7?w=400&h=300&fit=crop',
  },
  {
    id: 'coast-to-coast',
    name: 'Coast to Coast',
    query: 'Coast to Coast using B&Bs, group of 4',
    // VERIFIED: Yorkshire Dales with sheep and dry stone walls
    imageUrl: 'https://images.unsplash.com/photo-1669263042215-fb021e8aae1e?w=400&h=300&fit=crop',
  },
];

const FIT_OPTIONS: { value: FitPreference; label: string }[] = [
  { value: 'mens', label: 'Mens' },
  { value: 'womens', label: 'Womens' },
  { value: 'unisex', label: 'Unisex' },
];

const BUDGET_OPTIONS: { value: BudgetTier | undefined; label: string }[] = [
  { value: undefined, label: 'Auto' },
  { value: 'budget', label: 'Budget' },
  { value: 'mid', label: 'Mid-range' },
  { value: 'premium', label: 'Premium' },
];

const PRECIPITATION_OPTIONS: { value: ConditionOverrides['precipitation'] | undefined; label: string }[] = [
  { value: undefined, label: 'Auto' },
  { value: 'low', label: 'Low' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'high', label: 'High' },
];

const TEMPERATURE_OPTIONS: { value: ConditionOverrides['temperature_range'] | undefined; label: string }[] = [
  { value: undefined, label: 'Auto' },
  { value: 'warm', label: 'Warm' },
  { value: 'mild', label: 'Mild' },
  { value: 'cool', label: 'Cool' },
  { value: 'cold', label: 'Cold' },
];

export function QueryInput({ onSubmit, isLoading, initialQuery }: QueryInputProps) {
  const [query, setQuery] = useState(initialQuery || '');
  const [fit, setFit] = useState<FitPreference>('unisex');
  const [budgetTier, setBudgetTier] = useState<BudgetTier | undefined>(undefined);
  const [precipitation, setPrecipitation] = useState<ConditionOverrides['precipitation'] | undefined>(undefined);
  const [temperatureRange, setTemperatureRange] = useState<ConditionOverrides['temperature_range'] | undefined>(undefined);
  const [midges, setMidges] = useState<boolean | undefined>(undefined);
  const [showHistory, setShowHistory] = useState(false);
  const [showCustomization, setShowCustomization] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const historyRef = useRef<HTMLDivElement>(null);
  const customizationRef = useRef<HTMLDivElement>(null);
  const justSelectedHistory = useRef(false);

  const { history, addSearch, removeSearch } = useSearchHistory();

  // Update query when initialQuery changes (e.g., from route navigation)
  useEffect(() => {
    if (initialQuery) {
      setQuery(initialQuery);
    }
  }, [initialQuery]);

  // Close history dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (historyRef.current && !historyRef.current.contains(event.target as Node)) {
        setShowHistory(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close customization panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customizationRef.current && !customizationRef.current.contains(event.target as Node)) {
        setShowCustomization(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Quick Submit - uses defaults (selected fit, no budget override, no conditions)
  const handleQuickSubmit = () => {
    if (query.trim() && !isLoading) {
      addSearch(query.trim(), fit, undefined, undefined);
      setShowHistory(false);
      setShowCustomization(false);
      onSubmit({
        query: query.trim(),
        fit,
        budget_tier: undefined,
        conditions: undefined,
      });
    }
  };

  // Customized Submit - uses full options
  const handleCustomSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (query.trim() && !isLoading) {
      const conditions: ConditionOverrides | undefined =
        precipitation || temperatureRange || midges !== undefined
          ? { precipitation, temperature_range: temperatureRange, midges }
          : undefined;

      addSearch(query.trim(), fit, budgetTier, conditions);
      setShowHistory(false);
      setShowCustomization(false);
      onSubmit({
        query: query.trim(),
        fit,
        budget_tier: budgetTier,
        conditions,
      });
    }
  };

  // Legacy form submit - redirects to quick submit for Enter key
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    handleQuickSubmit();
  };

  const handleExampleClick = useCallback((exampleData: ExampleQueryData) => {
    setQuery(exampleData.query);
    inputRef.current?.focus();
  }, []);

  // Track image load errors for fallback
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const handleImageError = useCallback((id: string) => {
    setImageErrors(prev => new Set(prev).add(id));
  }, []);

  const handleHistoryClick = (item: SearchHistoryItem) => {
    setQuery(item.query);
    if (item.fit) setFit(item.fit);
    if (item.budget_tier) setBudgetTier(item.budget_tier);
    if (item.conditions) {
      setPrecipitation(item.conditions.precipitation);
      setTemperatureRange(item.conditions.temperature_range);
      setMidges(item.conditions.midges);
      if (item.conditions.precipitation || item.conditions.temperature_range || item.conditions.midges !== undefined) {
        setShowCustomization(true);
      }
    }
    setShowHistory(false);
    // Prevent onFocus from re-opening the dropdown immediately
    justSelectedHistory.current = true;
    inputRef.current?.focus();
    // Reset flag after focus event fires
    setTimeout(() => {
      justSelectedHistory.current = false;
    }, 0);
  };

  // Close dropdown when input loses focus (with delay to allow clicking history items)
  const handleInputBlur = () => {
    // Use timeout so click on history item can register before dropdown closes
    setTimeout(() => {
      setShowHistory(false);
    }, 150);
  };

  // Handle keyboard submission (Enter key) - important for mobile
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && query.trim() && !isLoading) {
      e.preventDefault();
      handleQuickSubmit();
    }
  };

  const hasActiveFilters = budgetTier || precipitation || temperatureRange || midges !== undefined;

  return (
    <div className="w-full max-w-2xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative" ref={historyRef}>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => !justSelectedHistory.current && history.length > 0 && setShowHistory(true)}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            placeholder="Describe your adventure..."
            disabled={isLoading}
            className="w-full px-5 py-4 text-base sm:text-lg bg-neutral-900 border border-neutral-700 rounded-xl text-white placeholder-neutral-500 outline-none transition-all duration-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          {query && !isLoading && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-300 transition-colors"
              aria-label="Clear input"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}

          {/* Search History Dropdown */}
          {showHistory && history.length > 0 && (
            <div className="absolute z-50 w-full mt-2 py-2 bg-neutral-900 border border-neutral-700 rounded-xl shadow-xl max-h-64 overflow-y-auto">
              <p className="px-4 py-1 text-xs text-neutral-500 uppercase tracking-wider">Recent searches</p>
              {history.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between px-4 py-2 hover:bg-neutral-800 cursor-pointer group"
                >
                  <button
                    type="button"
                    onClick={() => handleHistoryClick(item)}
                    className="flex-1 text-left text-neutral-300 hover:text-white truncate"
                  >
                    <span className="text-sm">{item.query}</span>
                    {(item.budget_tier || item.conditions) && (
                      <span className="ml-2 text-xs text-neutral-500">
                        {[
                          item.budget_tier,
                          item.conditions?.precipitation,
                          item.conditions?.temperature_range,
                          item.conditions?.midges ? 'midges' : null,
                        ].filter(Boolean).join(', ')}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSearch(item.id);
                    }}
                    className="ml-2 p-1 text-neutral-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Remove from history"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Fit Selector */}
        <div className="flex justify-center">
          <div className="flex rounded-lg bg-neutral-900 border border-neutral-700 p-1" role="group" aria-label="Select fit preference">
            {FIT_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setFit(option.value)}
                disabled={isLoading}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                  fit === option.value
                    ? 'bg-orange-500 text-black'
                    : 'text-neutral-400 hover:text-white hover:bg-neutral-800'
                }`}
                aria-pressed={fit === option.value}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Quick Search and Customize Buttons */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* Quick Search Button */}
          <button
            type="button"
            onClick={handleQuickSubmit}
            disabled={!query.trim() || isLoading}
            className="flex-1 px-6 py-3.5 bg-orange-500 hover:bg-orange-400 disabled:bg-neutral-700 disabled:cursor-not-allowed text-black font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-orange-500/20 hover:shadow-orange-500/30"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                <span>Building your kit...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span>Quick Search</span>
              </>
            )}
          </button>

          {/* Customize Dropdown */}
          <div className="relative" ref={customizationRef}>
            <button
              type="button"
              onClick={() => setShowCustomization(!showCustomization)}
              disabled={isLoading}
              className={`w-full sm:w-auto px-6 py-3.5 border font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                showCustomization || hasActiveFilters
                  ? 'bg-neutral-800 border-orange-500 text-orange-400'
                  : 'bg-neutral-900 border-neutral-700 text-neutral-300 hover:border-neutral-500 hover:text-white'
              }`}
            >
              <span>Customize</span>
              <svg
                className={`w-4 h-4 transition-transform ${showCustomization ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Customization Panel */}
            {showCustomization && (
              <div className="absolute z-40 right-0 sm:right-auto sm:left-0 mt-2 w-80 p-4 bg-neutral-900 border border-neutral-700 rounded-xl shadow-xl space-y-4 animate-fadeIn">
                {/* Budget Tier */}
                <div>
                  <label className="block text-xs text-neutral-500 uppercase tracking-wider mb-2">Budget</label>
                  <div className="flex flex-wrap gap-2">
                    {BUDGET_OPTIONS.map((option) => (
                      <button
                        key={option.label}
                        type="button"
                        onClick={() => setBudgetTier(option.value)}
                        disabled={isLoading}
                        className={`px-3 py-1.5 text-sm rounded-md border transition-all duration-200 disabled:opacity-50 ${
                          budgetTier === option.value
                            ? 'bg-orange-500 text-black border-orange-500'
                            : 'bg-neutral-800 text-neutral-400 border-neutral-700 hover:border-neutral-500 hover:text-white'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Conditions */}
                <div className="space-y-3">
                  {/* Rain */}
                  <div>
                    <label className="block text-xs text-neutral-500 uppercase tracking-wider mb-2">Rain</label>
                    <div className="flex flex-wrap gap-1">
                      {PRECIPITATION_OPTIONS.map((option) => (
                        <button
                          key={option.label}
                          type="button"
                          onClick={() => setPrecipitation(option.value)}
                          disabled={isLoading}
                          className={`px-2 py-1 text-xs rounded border transition-all duration-200 disabled:opacity-50 ${
                            precipitation === option.value
                              ? 'bg-orange-500 text-black border-orange-500'
                              : 'bg-neutral-800 text-neutral-400 border-neutral-700 hover:border-neutral-500 hover:text-white'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Temperature */}
                  <div>
                    <label className="block text-xs text-neutral-500 uppercase tracking-wider mb-2">Temperature</label>
                    <div className="flex flex-wrap gap-1">
                      {TEMPERATURE_OPTIONS.map((option) => (
                        <button
                          key={option.label}
                          type="button"
                          onClick={() => setTemperatureRange(option.value)}
                          disabled={isLoading}
                          className={`px-2 py-1 text-xs rounded border transition-all duration-200 disabled:opacity-50 ${
                            temperatureRange === option.value
                              ? 'bg-orange-500 text-black border-orange-500'
                              : 'bg-neutral-800 text-neutral-400 border-neutral-700 hover:border-neutral-500 hover:text-white'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Submit from Panel */}
                <button
                  type="button"
                  onClick={(e) => handleCustomSubmit(e as unknown as FormEvent)}
                  disabled={!query.trim() || isLoading}
                  className="w-full px-4 py-2.5 bg-orange-500 hover:bg-orange-400 disabled:bg-neutral-700 disabled:cursor-not-allowed text-black font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span>Get My Kit</span>
                </button>
              </div>
            )}
          </div>
        </div>

      </form>

      {/* Example Query Image Cards */}
      <div className="mt-8">
        <p className="text-xs text-neutral-500 uppercase tracking-wider mb-3 text-center">
          Try something like
        </p>
        <div
          data-testid="example-query-cards"
          className="grid grid-cols-2 lg:grid-cols-4 gap-3"
        >
          {EXAMPLE_QUERIES.map((example) => (
            <button
              key={example.id}
              onClick={() => handleExampleClick(example)}
              disabled={isLoading}
              aria-label={`Plan a trip: ${example.query}`}
              className="relative h-32 sm:h-40 rounded-lg overflow-hidden group cursor-pointer transition-transform duration-200 hover:scale-[1.03] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-neutral-950"
              data-has-image="true"
            >
              {/* Image or Gradient Fallback */}
              {!imageErrors.has(example.id) ? (
                <img
                  src={example.imageUrl}
                  alt={`${example.name} landscape`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  onError={() => handleImageError(example.id)}
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-orange-600 via-amber-700 to-neutral-900" />
              )}

              {/* Gradient Overlay */}
              <div
                data-testid="example-card-overlay"
                className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"
              />

              {/* Query Text - matches what gets populated in input */}
              <div className="absolute inset-0 flex flex-col justify-end p-3">
                <span className="font-mono font-bold text-xs sm:text-sm text-white drop-shadow-lg line-clamp-2">
                  {example.query}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
